import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Info, PartyPopper, AlertTriangle, Megaphone, X } from "lucide-react";
import { useAnnouncement, AnnouncementType } from "../../hooks/useAnnouncement";
import styles from "./AnnouncementBanner.module.css";

const TYPE_ICON: Record<AnnouncementType, React.ReactNode> = {
  info: <Info size={22} />,
  success: <PartyPopper size={22} />,
  warning: <AlertTriangle size={22} />,
  urgent: <Megaphone size={22} />,
};

const TYPE_CLASS: Record<AnnouncementType, string> = {
  info: styles.typeInfo,
  success: styles.typeSuccess,
  warning: styles.typeWarning,
  urgent: styles.typeUrgent,
};

function dismissKey(updatedAt: string) {
  return `announcement-dismissed-${updatedAt}`;
}

export function AnnouncementBanner() {
  const { announcement, isActive } = useAnnouncement();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!announcement) return;
    setDismissed(
      window.localStorage.getItem(dismissKey(announcement.updatedAt)) === "1",
    );
  }, [announcement]);

  if (!isActive || !announcement || !announcement.message || dismissed) return null;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(dismissKey(announcement.updatedAt), "1");
    } catch (error) {
      // localStorage may be unavailable (private browsing, etc.) — fine to ignore
    }
    setDismissed(true);
  };

  const isExternalLink = announcement.linkUrl.startsWith("http");

  return (
    <div className={`${styles.banner} ${TYPE_CLASS[announcement.type]}`}>
      <div className={styles.content}>
        {TYPE_ICON[announcement.type]}
        <span className={styles.text}>{announcement.message}</span>
        {announcement.linkUrl && announcement.linkText && (
          isExternalLink ? (
            <a
              href={announcement.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.linkBtn}
            >
              {announcement.linkText}
            </a>
          ) : (
            <Link to={announcement.linkUrl} className={styles.linkBtn}>
              {announcement.linkText}
            </Link>
          )
        )}
      </div>
      {announcement.dismissible && (
        <button
          className={styles.closeBtn}
          onClick={handleDismiss}
          aria-label="Dismiss announcement"
        >
          <X size={18} />
        </button>
      )}
    </div>
  );
}
